import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonitorSnapshot, useMonitorStream } from '@components/Pages/MonitorPage/hooks';
import { ProviderErrorResponse, UnauthorizedError } from '@apis/auth';
import { SiteNotLinkedError, UpstreamError } from '@apis/monitor';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { resetForSite } from '@store/slices/monitor.slice';
import {
  selectDashboardData,
  selectFloorSummaries,
  selectLiveSummary,
  selectSnapshotStatus,
  selectStaticTags,
  selectWorkforceRows,
} from '@store/selectors/monitor.selectors';
import { tradeLabel } from '@utils/tradeLabel';
import { deriveOperationsPanels, type OperationsPanels } from './operationsPanels';

/**
 * Right-rail state. `ready` carries the derived panels; `error` carries an
 * i18n key naming the reason; `loading` while the initial snapshot resolves.
 */
export type OperationsPanelsState =
  | { status: 'loading' }
  | { status: 'error'; reasonKey: string }
  | { status: 'ready'; panels: OperationsPanels };

function reasonKeyFor(error: unknown): string {
  if (error instanceof SiteNotLinkedError) return 'activities.panel.errorNotLinked';
  if (error instanceof ProviderErrorResponse) return 'activities.panel.errorProvider';
  if (error instanceof UpstreamError) return 'activities.panel.errorUnreachable';
  if (error instanceof UnauthorizedError) return 'activities.panel.errorGeneric';
  return 'activities.panel.errorGeneric';
}

/**
 * Wires the Activities & Issues right rail to the live Monitor feed: it
 * connects the same REST snapshot + SSE stream the Dashboard uses (populating
 * the shared `monitor` slice) and derives the three panels from the live
 * selectors. The panels update in real time as stream events arrive.
 */
export function useOperationsPanels(siteId: string): OperationsPanelsState {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (siteId) dispatch(resetForSite(siteId));
  }, [siteId, dispatch]);

  const snapshotQuery = useMonitorSnapshot(siteId || null);
  useMonitorStream(siteId || null);

  const snapshotStatus = useAppSelector(selectSnapshotStatus);
  const dashboardData = useAppSelector(selectDashboardData);
  const liveSummary = useAppSelector(selectLiveSummary);
  const workforceRows = useAppSelector(selectWorkforceRows);
  const floorSummaries = useAppSelector(selectFloorSummaries);
  const staticTags = useAppSelector(selectStaticTags);

  if (snapshotStatus === 'error') {
    return { status: 'error', reasonKey: reasonKeyFor(snapshotQuery.error) };
  }
  // Stay in `loading` until the SSE stream has delivered live presence data
  // (`dashboardData`) — not just the REST snapshot, which carries no
  // floors/workers. Rendering the panels earlier flashes a zero on-site count
  // against the real roster ("0 / 36 / 0%").
  if (snapshotStatus !== 'ready' || dashboardData === null) {
    return { status: 'loading' };
  }

  const panels = deriveOperationsPanels(
    {
      workersOnSite: liveSummary.workersOnSite,
      workforceRows,
      floorSummaries,
      staticTags,
    },
    (id) => tradeLabel(t, id),
  );
  return { status: 'ready', panels };
}
